import {CommentDiscussionIcon, DotFillIcon, MoonIcon, ShieldIcon, SyncIcon, PeopleIcon} from '@primer/octicons-react'
import type {
  ReviewRequestsData,
  CompletedReview,
  PendingReviewRequest,
} from '../../../page-data/payloads/review-requests'
import styles from './Reviewers.module.css'
import {pullRequestsOverviewRoute} from '../../../routes/overview-route'
import {pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {IconButton, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, memo} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {Link} from '@github-ui/react-core/link'
import {ReviewersLoader} from './ReviewersLoader'
import {ReviewerIdentity} from './ReviewerIdentity'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {reviewState, reviewedAtLabel, codeOwnerTooltip, deferredTooltip} from './ReviewersShared'

interface ReviewersProps {
  reviewers: ReviewRequestsData
}

export function Reviewers() {
  return (
    <ErrorBoundary
      boundaryName="Reviewers"
      fallback={<span className={styles.emptyState}>Unable to load reviewers</span>}
    >
      <Suspense fallback={<UserSectionSkeleton placeholderRows={2} />}>
        <ReviewersLoader>{reviewers => <ReviewersComponent reviewers={reviewers} />}</ReviewersLoader>
      </Suspense>
    </ErrorBoundary>
  )
}

/** Full reviewers section on the Overview page */
export function ReviewersComponent({reviewers}: ReviewersProps) {
  const completedReviews = reviewers.completedReviews ?? []
  const pendingRequests = reviewers.pendingReviewRequests ?? []

  if (completedReviews.length === 0 && pendingRequests.length === 0) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  return (
    <ul className={styles.fullReviewerList}>
      {completedReviews.map(review => (
        <CompletedReviewRow key={`completed-${review.id}`} review={review} />
      ))}
      {pendingRequests.map(request => (
        <PendingReviewRow key={`pending-${request.reviewer.id}`} request={request} />
      ))}
    </ul>
  )
}

const CompletedReviewRow = memo(function CompletedReviewRow({review}: {review: CompletedReview}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsOverviewRoute)
  const name = review.reviewer.displayLogin
  const {icon: StateIcon, className: stateClass, label: stateLabel} = reviewState(review.state)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })
  const unresolvedCount = review.threadCounts.total - review.threadCounts.resolved
  const hasThreads = review.threadCounts.total > 0

  return (
    <li className={clsx(styles.fullReviewerRow)}>
      <Link
        to={changesWithRangePath}
        className={clsx(styles.stateBadge, stateClass)}
        aria-label={`${name}: ${stateLabel}`}
      >
        <StateIcon size={16} />
      </Link>
      <div className={styles.rowContent}>
        <div className={styles.fullReviewerHeader}>
          <span className={styles.reviewerWithTime}>
            <ReviewerIdentity reviewer={review.reviewer} />
            {review.submittedAt && (
              <span className={styles.reviewedAt}>
                {reviewedAtLabel(review.state)} <RelativeTime datetime={review.submittedAt} />
              </span>
            )}
          </span>
          <span className={styles.threadWrapper}>
            {hasThreads && (
              <span className={styles.threadRow}>
                <CommentDiscussionIcon size={16} className="color-fg-muted" />
                <span className={styles.threadCount}>
                  {unresolvedCount > 0 ? (
                    <>
                      {review.threadCounts.total} comments,{' '}
                      <span className={styles.unresolvedCount}>{unresolvedCount} unresolved</span>
                    </>
                  ) : (
                    <>{review.threadCounts.resolved} resolved comments</>
                  )}
                </span>
              </span>
            )}
          </span>
          <span className={clsx(styles.fullIndicators, 'mr-n1')}>
            {review.canReRequest && (
              <IconButton
                icon={SyncIcon}
                aria-label={`Re-request review from ${name}`}
                variant="invisible"
                size="small"
                tooltipDirection="nw"
                className="color-fg-muted"
              />
            )}
          </span>
        </div>

        {review.onBehalfOfTeamNames && review.onBehalfOfTeamNames.length > 0 && (
          <span className={styles.delegationNote}>
            <PeopleIcon size={12} />
            On behalf of{' '}
            {review.onBehalfOfTeamNames.map((team, i) => (
              <span key={team}>
                {i > 0 ? <span>, </span> : null}
                <span className={styles.delegationTeamName}>{team}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </li>
  )
})

const PendingReviewRow = memo(function PendingReviewRow({request}: {request: PendingReviewRequest}) {
  return (
    <li className={styles.fullReviewerRow}>
      <span className={clsx(styles.stateBadge, 'color-fg-attention')} role="img" aria-label="Awaiting review">
        <DotFillIcon size={16} />
      </span>
      <div className={styles.rowContent}>
        <div className={styles.fullReviewerHeader}>
          <span className={styles.reviewerWithTime}>
            <ReviewerIdentity reviewer={request.reviewer} />
            {request.deferredType === null && request.createdAt && (
              <span className={styles.reviewedAt}>
                was requested for review <RelativeTime datetime={request.createdAt} />
              </span>
            )}
          </span>
          <span className={styles.fullIndicators}>
            {request.deferredType !== null && (
              <IconButton
                icon={MoonIcon}
                aria-label={deferredTooltip(request.deferredType, request.reviewer.displayLogin)}
                variant="invisible"
                size="small"
                tooltipDirection="nw"
                className={clsx(styles.statusIcon, 'color-fg-muted')}
              />
            )}
            {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
              <IconButton
                icon={ShieldIcon}
                aria-label={codeOwnerTooltip(request)}
                variant="invisible"
                size="small"
                tooltipDirection="nw"
                className={clsx(styles.statusIcon, 'color-fg-muted')}
              />
            )}
          </span>
        </div>

        {request.delegatedFromTeamName && (
          <span className={styles.delegationNote}>
            <PeopleIcon size={12} />
            Assigned from <span className={styles.delegationTeamName}>{request.delegatedFromTeamName}</span>
          </span>
        )}
      </div>
    </li>
  )
})
